import React, { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BadgeCheckIcon, ClockIcon, MapPinIcon, MessageSquareIcon, ShieldAlertIcon, StarIcon } from 'lucide-react';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Panel } from '../../../components/ui/Panel';
import { FeatureGate } from '../../../components/platform/FeatureStatusBadge';
import { ReportControls } from '../../../components/trust/ReportControls';
import { marketCategories, marketItems, sellerFor } from '../../../data/market';
import { naira } from '../../../utils/format';

/**
 * One listing in full. Every category shares this page because the *decision*
 * a buyer makes is the same shape — what is it, who is selling it, what is
 * actually guaranteed, what happens to my money — even though the facts that
 * matter differ by category.
 */
export function ItemDetail() {
  const {
    itemId
  } = useParams<{
    itemId: string;
  }>();
  const item = marketItems.find(entry => entry.id === itemId);
  const [messaged, setMessaged] = useState(false);
  if (!item) return <Navigate to="/market" replace data-id="element-11046" data-mp="tzfa3gbzlpf3" />;
  const seller = sellerFor(item.sellerId);
  const category = marketCategories.find(entry => entry.id === item.category);
  const soldOut = item.availability === 'Sold out' || item.availability === 'Closed now';
  const facts = [item.condition && {
    label: 'Condition',
    value: item.condition
  }, item.courseCodes?.length && {
    label: 'Used for',
    value: item.courseCodes.join(', ')
  }, item.prepMinutes !== undefined && {
    label: 'Preparation',
    value: `About ${item.prepMinutes} minutes`
  }, item.distanceKm !== undefined && {
    label: 'Distance to main gate',
    value: `${item.distanceKm} km, straight line`
  }, item.area && {
    label: 'Area',
    value: item.area
  }, item.deliveryLabel && {
    label: 'Delivery',
    value: item.deliveryLabel
  }, {
    label: 'Availability',
    value: item.availability
  }].filter(Boolean) as {
    label: string;
    value: string;
  }[];
  return <div className="space-y-8 pb-8" data-id="element-11047" data-mp="3uvzg1ym2sw5i">
      <div data-id="element-11048" data-mp="3uz3sfj9p2y79">
        <Link to={category?.to ?? '/market'} className="inline-flex min-h-touch items-center gap-1.5 text-body-sm font-medium text-ink-muted hover:text-ink" data-id="element-11049" data-mp="3uyvd7kq3ngsw">
          <ArrowLeftIcon aria-hidden className="h-4 w-4" data-id="element-11050" data-mp="3l54tsu8txwk2" />
          {category?.label ?? 'Marketplace'}
        </Link>
      </div>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]" data-id="element-11051" data-mp="3c69orlhiusic">
        <div className="space-y-6" data-id="element-11052" data-mp="3c46om8mnnizw">
          {item.imageUrl && <div className="overflow-hidden rounded-panel border border-line bg-canvas" data-id="element-11053" data-mp="2xatq135zy8fm">
              <img src={item.imageUrl} alt={item.title} className="aspect-[16/10] w-full object-cover" data-id="element-11054" data-mp="329509f07ekyd" />
            </div>}

          <div data-id="element-11055" data-mp="10fp82dogwy1a">
            <h1 className="text-h1 font-semibold tracking-tight text-ink" data-id="element-11056" data-mp="10g7chxlaaika">{item.title}</h1>
            <p className="mt-3 max-w-[62ch] text-body-lg text-ink-muted" data-id="element-11057" data-mp="2kzkjtqcpvwy7">{item.summary}</p>
          </div>

          <Panel title="Details" data-id="element-11058" data-mp="2g1rfz3yvzf13">
            <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2" data-id="element-11059" data-mp="2uoudhan7lnof">
              {facts.map(fact => <div key={fact.label} data-id="element-11060" data-mp="2pye0frnaw42l">
                  <dt className="text-caption text-ink-muted" data-id="element-11061" data-mp="2pxcbqkdd3j2e">{fact.label}</dt>
                  <dd className="mt-0.5 text-body font-medium text-ink" data-id="element-11062" data-mp="33or8du06zirx">{fact.value}</dd>
                </div>)}
            </dl>
          </Panel>

          <Panel title="What Campuzon does and does not guarantee" description="Read this before you pay anyone." data-id="element-11063" data-mp="2zq9wyy9qnm4p">
            <ul className="space-y-3 text-body-sm text-ink" data-id="element-11064" data-mp="38pnaq34wawv8">
              <li className="flex items-start gap-2.5" data-id="element-11065" data-mp="1j9nr1s3nhp1c">
                <ShieldAlertIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ink-muted" data-id="element-11066" data-mp="1ja7axecv2ni0" />
                <span data-id="element-11067" data-mp="1ec5jo65a7hvg">
                  {seller?.verified ? 'Campuzon has checked who this seller is. That is a check on identity, not a promise about this particular item or a guarantee of a refund.' : 'Campuzon has not inspected this seller. Meet in a public place on campus, and do not pay in advance outside Campuzon.'}
                </span>
              </li>
              <li className="flex items-start gap-2.5" data-id="element-11068" data-mp="1uo91z91q3o9z">
                <ShieldAlertIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ink-muted" data-id="element-11069" data-mp="x21lp1w247hs" />
                <span data-id="element-11070" data-mp="11zjxr15df2u0">
                  Ratings come from people who completed an order. They are not moderated into a
                  quality score, and a small number of reviews means very little.
                </span>
              </li>
            </ul>
            <div className="mt-4" data-id="element-11071" data-mp="1brzfpygo3rew">
              <ReportControls subject={item.title} surface="listing" data-id="element-11072" data-mp="ebai6gyr3bbb" />
            </div>
          </Panel>
        </div>

        {/* Decision rail. Price, seller, action. */}
        <div className="space-y-5 lg:sticky lg:top-24 lg:self-start" data-id="element-11073" data-mp="2wk50jldn27yq">
          <Panel data-id="element-11074" data-mp="2wjc2d8sqwqs7">
            {item.priceNaira !== undefined ? <p className="cz-num text-display font-semibold tracking-tight text-ink" data-id="element-11075" data-mp="2sjl3siwz96d4">
                {naira(item.priceNaira)}
                {item.priceUnit && item.priceUnit !== 'each' && <span className="ml-1.5 text-body font-normal text-ink-muted" data-id="element-11076" data-mp="2ijaq6x715wxf">
                    {item.priceUnit}
                  </span>}
              </p> : <p className="text-h3 font-semibold text-ink" data-id="element-11077" data-mp="28uamj0hl2dcw">Quoted per job</p>}

            <div className="mt-4 flex flex-wrap gap-2.5" data-id="element-11078" data-mp="24s5xyg5zs6x4">
              <Button variant="primary" disabled={soldOut} data-id="element-11079" data-mp="24ql30pfrmlyq">
                {soldOut ? item.availability : 'Continue'}
              </Button>
              <Button variant="secondary" icon={<MessageSquareIcon className="h-4 w-4" data-id="element-11081" data-mp="1uwm7i9a760fr" />} onClick={() => setMessaged(true)} data-id="element-11080" data-mp="1uv18o7gunvc3">
                {messaged ? 'Message opened' : 'Message seller'}
              </Button>
            </div>
            {messaged && <p className="mt-3 text-body-sm text-ink-muted" data-id="element-11082" data-mp="1l7yocklzl8h5">
                This conversation lives in Community → Messages with the listing attached, so you
                both know what it refers to.
              </p>}

            <div className="mt-5 border-t border-line pt-4" data-id="element-11083" data-mp="1basw391mzbfc">
              <FeatureGate featureKey="escrow" inline data-id="element-11084" data-mp="1yzvg2njqtgd0" />
            </div>
          </Panel>

          {seller && <Panel title="Seller" data-id="element-11085" data-mp="23wvhgl5nduxs">
              <Link to={`/market/provider/${seller.id}`} className="text-h4 font-semibold text-ink hover:text-brand-700" data-id="element-11086" data-mp="1q6ib2n8emoak">
                {seller.displayName}
              </Link>
              <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-11087" data-mp="2hwde1mp6qchp">
                {seller.kind} · {seller.campus}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-11088" data-mp="2mpfx30wc63jn">
                {seller.verified ? <Badge tone="ok" data-id="element-11089" data-mp="b72xosuwnrkw">
                    <BadgeCheckIcon aria-hidden className="h-4 w-4" data-id="element-11090" data-mp="6ambw5b6hpaz" />
                    Verified provider
                  </Badge> : <Badge tone="warn" data-id="element-11091" data-mp="ky7hq6zfu2ml">Not inspected by Campuzon</Badge>}
                {seller.rating !== undefined && <span className="cz-num inline-flex items-center gap-1 text-caption text-ink-muted" data-id="element-11092" data-mp="3offx8k6p0qe5">
                    <StarIcon aria-hidden className="h-4 w-4 text-warn-700" data-id="element-11093" data-mp="3jh4ibv1jjpp2" />
                    {seller.rating.toFixed(1)} ({seller.reviewCount})
                  </span>}
              </div>
              <ul className="mt-3 space-y-1.5 text-caption text-ink-muted" data-id="element-11094" data-mp="3teiz5vrdmghw">
                {seller.responseRateLabel && <li className="inline-flex items-center gap-1.5" data-id="element-11095" data-mp="1dm386tyeyyax">
                    <MessageSquareIcon aria-hidden className="h-4 w-4" data-id="element-11096" data-mp="18pcta9yci8r3" />
                    {seller.responseRateLabel}
                  </li>}
                {seller.hoursLabel && <li className="flex items-center gap-1.5" data-id="element-11097" data-mp="375lp93iijn66">
                    <ClockIcon aria-hidden className="h-4 w-4" data-id="element-11098" data-mp="3740sd78q7s55" />
                    {seller.hoursLabel}
                  </li>}
                <li className="flex items-center gap-1.5" data-id="element-11099" data-mp="3gx889m13eqjh">
                  <MapPinIcon aria-hidden className="h-4 w-4" data-id="element-11100" data-mp="3lv9qy5ulwnuf" />
                  {seller.campus}
                </li>
              </ul>
              <Link to={`/market/provider/${seller.id}`} className="mt-4 inline-flex min-h-touch items-center text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-11101" data-mp="3pw3bcp8ubkik">
                See everything from this seller
              </Link>
            </Panel>}
        </div>
      </div>
    </div>;
}